// The discriminator-to-template registry. It lives in constants/ because it is
// pure data importing nothing above it, matching constants/query.ts.
//
// It maps to a template IDENTIFIER, never to a component. The dependency law
// forbids constants/ importing components/, and that constraint is the right
// answer anyway: 16-22 owns the modules and resolves an id to one, so adding a
// template is an entry here plus a module there.

export const FLOW_TEMPLATE_IDS = [
  "SCHOOLFEE_SEARCH",
  "SCHOOLFEE_MANUAL",
  "SCHOOLFEE_REGISTRATION",
  "DEPOSITFUNDS_SEARCH",
  "DEPOSITFUNDS_MANUAL",
] as const;

export type FlowTemplateId = (typeof FLOW_TEMPLATE_IDS)[number];

// The tuple the tier-2 service response carries. Deliberately not a fixed
// three-slot form: D-01's subject/item/instrument decomposition describes
// DEPOSITFUND and SCHOOLFEE and is not a universal contract, so a template
// owns its own step sequence and a future service type may bring another.
export type FlowDiscriminator = {
  service_type: string;
  variant: string | null;
  is_searchable: boolean;
};

export type FlowTemplateMatch = {
  matched: true;
  templateId: FlowTemplateId;
  // True only when is_searchable could not be resolved and D-11's fail-closed
  // default chose this template. A correctly resolved false is not a fallback,
  // and counting it as one would make the signal useless.
  failedClosed: boolean;
};

export type FlowTemplateEmpty = { matched: false };

export type FlowTemplateResult = FlowTemplateMatch | FlowTemplateEmpty;

// D-02: an unmatched combination yields THIS, a value the caller must handle.
// Not undefined, which a caller can coerce; not the first entry; not a default
// template, which is how a payer lands in the wrong journey with no error.
export const NO_FLOW_TEMPLATE: FlowTemplateEmpty = Object.freeze({
  matched: false,
});

// `variant` is nullable, and "no variant" must not collide with the empty
// string: both would key the same row and silently match the null entry,
// which is the coincidental default D-02 exists to forbid. NONE is a token no
// variant value can be, because CORE's variants are identifier-shaped.
const NO_VARIANT = "<none>";

const key = (
  serviceType: string,
  variant: string | null,
  isSearchable: boolean
): string => `${serviceType}|${variant ?? NO_VARIANT}|${isSearchable}`;

// Keyed on the discriminator triple, greppable and enumerable as data.
export const FLOW_TEMPLATES: Readonly<Record<string, FlowTemplateId>> =
  Object.freeze({
    [key("SCHOOLFEE", "FEE", true)]: "SCHOOLFEE_SEARCH",
    [key("SCHOOLFEE", "FEE", false)]: "SCHOOLFEE_MANUAL",
    // A registration service has no roster to search, so it never has the
    // search journey. The invariant is enforced server-side (MPAY-32050); this
    // entry means a wrong value cannot produce a search journey here either.
    //
    // It is its OWN template, not the manual one: registration collects no
    // matricule and its amount is read-only and paid in full, so it is a
    // different step sequence, which is what D-01 says a template owns.
    // Branching on variant inside one template would put back the runtime
    // decision D-02 exists to remove.
    [key("SCHOOLFEE", "REGISTRATION", false)]: "SCHOOLFEE_REGISTRATION",
    // DEPOSITFUNDS is plural: it is the value the domain writes
    // (corporate_subsidiary_service/entity.go), and the singular this row
    // carried before matched nothing CPS sends. The ids are spelled the same
    // way on purpose -- a singular id beside a plural key is how the miss got
    // in, and it would read as correct a second time.
    //
    // Both journeys exist today and are chosen at paymentPage.tsx by the same
    // flag V81 carries into is_searchable, so a single row could only serve
    // one of them.
    [key("DEPOSITFUNDS", null, true)]: "DEPOSITFUNDS_SEARCH",
    [key("DEPOSITFUNDS", null, false)]: "DEPOSITFUNDS_MANUAL",
  });

export const resolveFlowTemplate = (
  service: FlowDiscriminator
): FlowTemplateResult => {
  // D-11: an unresolvable discriminator fails closed to manual entry, which is
  // always renderable and never blocks a payment. This is a defensive default
  // and not the fix -- 16-14 is the fix -- so it is reported rather than
  // silently applied: a wrong journey raises no exception and no failed
  // request, and this flag is the only signal that can exist.
  const unresolved = typeof service.is_searchable !== "boolean";
  const isSearchable = unresolved ? false : service.is_searchable;

  const templateId =
    FLOW_TEMPLATES[key(service.service_type, service.variant, isSearchable)];

  if (!templateId) return NO_FLOW_TEMPLATE;

  return { matched: true, templateId, failedClosed: unresolved };
};
